import React, { createContext, useContext, useState, ReactNode } from 'react';

export interface QuestionnaireAnswers {
  [key: string]: string | string[] | null;
}

// Compact structure used for recommendations API
export interface SurveyAnswerForRecommendations {
  questionId: string;
  optionIds: string[];
  answerNumber: number | null;
}

interface QuestionnaireContextType {
  answers: QuestionnaireAnswers;
  setAnswers: (answers: QuestionnaireAnswers | ((prev: QuestionnaireAnswers) => QuestionnaireAnswers)) => void;
  clearAnswers: () => void;
  responseId: string | null;
  setResponseId: (id: string | null) => void;
  surveyAnswersForRecommendations: SurveyAnswerForRecommendations[];
  setSurveyAnswersForRecommendations: (answers: SurveyAnswerForRecommendations[]) => void;
}

const QuestionnaireContext = createContext<QuestionnaireContextType | undefined>(undefined);

export const useQuestionnaire = () => {
  const context = useContext(QuestionnaireContext);
  if (!context) {
    throw new Error('useQuestionnaire must be used within a QuestionnaireProvider');
  }
  return context;
};

interface QuestionnaireProviderProps {
  children: ReactNode;
}

export const QuestionnaireProvider: React.FC<QuestionnaireProviderProps> = ({ children }) => {
  const [answers, setAnswersState] = useState<QuestionnaireAnswers>({});
  const [responseId, setResponseIdState] = useState<string | null>(null);
  const [surveyAnswersForRecommendations, setSurveyAnswersForRecommendationsState] = useState<
    SurveyAnswerForRecommendations[]
  >([]);

  const setAnswers = (value: QuestionnaireAnswers | ((prev: QuestionnaireAnswers) => QuestionnaireAnswers)) => {
    if (typeof value === 'function') {
      setAnswersState(value);
    } else {
      setAnswersState(value);
    }
  };

  const clearAnswers = () => {
    setAnswersState({});
    setResponseIdState(null);
    setSurveyAnswersForRecommendationsState([]);
  };

  return (
    <QuestionnaireContext.Provider
      value={{
        answers,
        setAnswers,
        clearAnswers,
        responseId,
        setResponseId: setResponseIdState,
        surveyAnswersForRecommendations,
        setSurveyAnswersForRecommendations: setSurveyAnswersForRecommendationsState,
      }}
    >
      {children}
    </QuestionnaireContext.Provider>
  );
};
